import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, GoogleButton, Divider } from "@/pages/auth/AuthShell";

export default function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { applyMe } = useAuth();
  const next = params.get("next") || "/app";
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const done = (data) => {
    applyMe(data);
    navigate(data.user?.is_super_admin && next === "/app" && !data.memberships?.length ? "/admin" : next, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      done(data);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to your Clockaroo account." testId="login-page">
        <>
          <GoogleButton next={next} />
          <Divider />
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            {error && <Alert variant="destructive" data-testid="login-error"><AlertDescription>{error}</AlertDescription></Alert>}
            <div className="space-y-1.5"><Label htmlFor="email">Email</Label><Input id="email" type="email" required autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-xl" data-testid="login-email-input" /></div>
            <div className="space-y-1.5">
              <div className="flex justify-between"><Label htmlFor="password">Password</Label><Link to="/forgot-password" className="text-sm font-medium text-[#00A85C] hover:underline" data-testid="forgot-password-link">Forgot password?</Link></div>
              <Input id="password" type="password" required autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 rounded-xl" data-testid="login-password-input" />
            </div>
            <Button disabled={busy} className="h-11 w-full rounded-xl bg-[#00C16B] hover:bg-[#00A85C]" data-testid="login-submit-btn">{busy ? "Logging in…" : "Log in"}</Button>
          </form>
          <p className="mt-6 text-sm text-slate-600">New to Clockaroo? <Link to={`/register${params.get("next") ? `?next=${encodeURIComponent(next)}` : ""}`} className="font-semibold text-[#00A85C] hover:underline" data-testid="register-link">Register your business</Link></p>
        </>
    </AuthShell>
  );
}
